/**
 * Module CEE — Simulations & Dossiers.
 * V1 couvre uniquement BAR-TH-171 (résidentiel PAC air/eau ou eau/eau)
 * et BAT-TH-134 (tertiaire HP flottante groupe froid).
 * Le catalogue est extensible côté backend (cee_fiches_catalog.py).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Zap, Plus, FileSearch, Copy, ArrowRight, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABELS = {
  draft: 'Brouillon',
  completed: 'Terminée',
  converted: 'Convertie',
};

const STATUS_COLORS = {
  draft: 'bg-amber-100 text-amber-800',
  completed: 'bg-emerald-100 text-emerald-800',
  converted: 'bg-blue-100 text-blue-800',
};

export default function CeeSimulations() {
  const nav = useNavigate();
  const [sims, setSims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSecteur, setFilterSecteur] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterSecteur) params.secteur = filterSecteur;
      if (search) params.search = search;
      const r = await api.get('/cee/simulations', { params });
      setSims(r.data.simulations || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement simulations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterSecteur]);

  const duplicate = async (id) => {
    try {
      const r = await api.post(`/cee/simulations/${id}/duplicate`);
      toast.success('Simulation dupliquée');
      nav(`/cee/simulations/${r.data.simulation_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur duplication');
    }
  };

  const removeSim = async (s) => {
    const name = s.label || s.simulation_id;
    if (!window.confirm(`Supprimer la simulation "${name}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/cee/simulations/${s.simulation_id}`);
      toast.success('Simulation supprimée');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="cee-simulations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Simulations CEE</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Estimez les certificats d'économie d'énergie et convertissez en dossier.
          </p>
        </div>
        <Button
          data-testid="cee-new-simulation-btn"
          className="bg-[#1C49A6] hover:bg-[#173B85]"
          onClick={() => nav('/cee/simulations/new')}
        >
          <Plus className="w-4 h-4 mr-2" /> Nouvelle simulation
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 flex flex-wrap gap-3 items-center border-slate-200">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="cee-simu-search"
            placeholder="Rechercher (libellé, fiche…)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            className="pl-9"
          />
        </div>
        <select
          data-testid="cee-simu-status-filter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm"
        >
          <option value="">Tous statuts</option>
          <option value="draft">Brouillon</option>
          <option value="completed">Terminée</option>
          <option value="converted">Convertie</option>
        </select>
        <select
          data-testid="cee-simu-secteur-filter"
          value={filterSecteur}
          onChange={(e) => setFilterSecteur(e.target.value)}
          className="border border-slate-200 rounded-md px-3 py-2 text-sm"
        >
          <option value="">Tous secteurs</option>
          <option value="residentiel">Résidentiel</option>
          <option value="tertiaire">Tertiaire</option>
        </select>
      </Card>

      {/* Liste */}
      <Card className="border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Chargement…</div>
        ) : sims.length === 0 ? (
          <div className="p-12 text-center">
            <Zap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 mb-4">Aucune simulation pour l'instant.</p>
            <Button
              onClick={() => nav('/cee/simulations/new')}
              className="bg-[#1C49A6] hover:bg-[#173B85]"
            >
              Créer votre première simulation
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cee-simu-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Libellé</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Fiche</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Engagement</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Statut</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">kWh cumac</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-emerald-700 text-xs uppercase">Prime max</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600 text-xs uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sims.map((s) => (
                  <tr key={s.simulation_id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link
                        to={`/cee/simulations/${s.simulation_id}`}
                        className="text-slate-900 hover:text-[#1C49A6] font-medium"
                      >
                        {s.label || s.simulation_id}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      <span className="font-mono text-xs">{s.fiche_code}</span>
                      <span className="text-slate-400 text-xs ml-1">v{s.fiche_version}</span>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{s.engagement_date || '—'}</td>
                    <td className="px-4 py-2">
                      <Badge className={STATUS_COLORS[s.status] + ' border-0'}>
                        {STATUS_LABELS[s.status] || s.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {s.result?.kwh_cumac != null
                        ? new Intl.NumberFormat('fr-FR').format(s.result.kwh_cumac)
                        : <span className="text-amber-600 text-xs">à vérifier</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-bold text-emerald-700">
                      {s.best_prime_eur != null
                        ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(s.best_prime_eur)
                        : <span className="text-slate-400 text-xs">—</span>}
                      {s.tarif_type_recommande === 'precarite' && s.best_prime_eur != null && (
                        <div className="text-[10px] text-orange-600 font-normal">précarité</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm"
                        onClick={() => duplicate(s.simulation_id)}
                        data-testid={`cee-simu-duplicate-${s.simulation_id}`}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => nav(`/cee/simulations/${s.simulation_id}`)}
                        data-testid={`cee-simu-open-${s.simulation_id}`}>
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm"
                        onClick={() => removeSim(s)}
                        title={s.status === 'converted' ? 'Convertie en dossier — supprimer d\'abord le dossier' : 'Supprimer'}
                        disabled={s.status === 'converted' && !!s.dossier_id}
                        className="text-red-600 hover:bg-red-50 disabled:opacity-40"
                        data-testid={`cee-simu-delete-${s.simulation_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
